import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Shield } from 'lucide-react';

export const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl bg-slate-900 border-slate-700 text-gray-100">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <Shield className="w-5 h-5 text-amber-400" />
            Privacy Policy
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="space-y-6 text-sm text-gray-300">
            <p className="text-gray-400 text-xs">
              Last updated: December 2024
            </p>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">1. Information We Collect</h3>
              <p className="mb-2">When you use MysHaps, we collect:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Account Information:</strong> Email address and name provided during sign-up through our authentication provider (Clerk)</li>
                <li><strong>Game Data:</strong> Your game progress, case sessions, and gameplay choices</li>
                <li><strong>Usage Data:</strong> Basic analytics to understand how the app is used</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">2. How We Use Your Information</h3>
              <p className="mb-2">Your information is used solely for:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Authentication:</strong> To create and manage your account</li>
                <li><strong>Game Functionality:</strong> To save your progress and provide a personalized experience</li>
                <li><strong>Development:</strong> To improve the app and fix issues</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">3. Data Sharing</h3>
              <p className="mb-2">
                <strong className="text-amber-400">We will never sell, rent, or share your personal data with third parties for marketing purposes.</strong>
              </p>
              <p>
                Your data is only shared with:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 mt-2">
                <li><strong>Clerk:</strong> Our authentication provider, which handles secure sign-in</li>
                <li><strong>Infrastructure Providers:</strong> Services that host and run the application (e.g., database, hosting)</li>
              </ul>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">4. Data Security</h3>
              <p>
                We implement industry-standard security measures to protect your data, including encrypted connections (HTTPS) and secure authentication through Clerk. Your game data is stored securely and is not publicly accessible.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">5. Your Rights</h3>
              <p className="mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Delete:</strong> Request deletion of your account and associated data</li>
                <li><strong>Withdraw:</strong> Sign out and stop using the service at any time</li>
              </ul>
              <p className="mt-2">
                To exercise these rights, please contact us using the information below.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">6. Data Retention</h3>
              <p>
                Your game data is retained as long as your account is active. If you delete your account, your personal data will be removed within 30 days, though anonymized analytics may be retained.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">7. Children's Privacy</h3>
              <p>
                MysHaps is intended for users aged 13 and older. We do not knowingly collect personal information from children under 13.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">8. Changes to This Policy</h3>
              <p>
                We may update this privacy policy from time to time. Significant changes will be communicated through the app.
              </p>
            </section>

            <section>
              <h3 className="text-base font-semibold text-white mb-2">9. Contact Us</h3>
              <p>
                If you have questions about this privacy policy or your data, please contact us at:{' '}
                <a href="mailto:deandrae@gmail.com" className="text-amber-400 hover:text-amber-300 underline">
                  deandrae@gmail.com
                </a>
              </p>
            </section>

            <div className="pt-4 border-t border-slate-700 text-center text-gray-500 text-xs">
              MysHaps - Mystery Happens, Inc.
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicyModal;
